import { Edit2, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  useGetMany,
  useListContext,
  useNotify,
  useRefresh,
  useUpdate,
  useGetList,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Contact, Company } from "../types";

export function BulkEditButton() {
  const notify = useNotify();
  const refresh = useRefresh();
  const { connectionPipelineStages } = useConfigurationContext();
  const [update] = useUpdate<Contact>("contacts", undefined, {
    returnPromise: true,
  });

  const { onUnselectItems, selectedIds = [] } = useListContext<Contact>();
  const [open, setOpen] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  // Selected field and value
  const [selectedField, setSelectedField] = useState<string>("");
  const [selectedValue, setSelectedValue] = useState<string>("");

  const { data: selectedContacts = [], isPending: isPendingContacts } =
    useGetMany<Contact>(
      "contacts",
      { ids: selectedIds },
      { enabled: open && selectedIds.length > 0 },
    );

  const { data: companies = [], isPending: isPendingCompanies } =
    useGetList<Company>(
      "companies",
      {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "name", order: "ASC" },
      },
      { enabled: open && selectedField === "company_id" },
    );

  const closeDialog = useCallback(() => {
    setOpen(false);
    setSelectedField("");
    setSelectedValue("");
  }, []);

  useEffect(() => {
    if (!selectedIds.length && open) {
      closeDialog();
    }
  }, [closeDialog, open, selectedIds.length]);

  // Reset selected value when field changes
  useEffect(() => {
    setSelectedValue("");
  }, [selectedField]);

  const fields = [
    { id: "connection_pipeline_stage", label: "Connection Stage" },
    { id: "relationship_motion", label: "Relationship Type" },
    { id: "status", label: "Strength (Status)" },
    { id: "influence", label: "Influence (Purchasing Power)" },
    { id: "next_relationship_move", label: "Next Move" },
    { id: "company_id", label: "Company" },
  ];

  const relationshipStrengthChoices = [
    { id: "A", name: "A - Strong (Pick up phone)" },
    { id: "B", name: "B - Medium (Recognize name)" },
    { id: "C", name: "C - Weak (Peripheral/Unknown)" },
  ];

  const influenceChoices = [
    { id: "1", name: "1 - Decision Maker (Power of the purse)" },
    { id: "2", name: "2 - Influencer (Can refer/connector)" },
    { id: "3", name: "3 - Neither" },
  ];

  const nextRelationshipMoveChoices = [
    { id: "Comment", name: "Comment" },
    { id: "Connect", name: "Connect" },
    { id: "DM", name: "DM" },
    { id: "Email", name: "Email" },
    { id: "Useful Resource", name: "Useful Resource" },
    { id: "Intro Ask", name: "Intro Ask" },
    { id: "Referral Ask", name: "Referral Ask" },
    { id: "Podcast Invite", name: "Podcast Invite" },
    { id: "Meeting Prep", name: "Meeting Prep" },
    { id: "Wait", name: "Wait" },
    { id: "Disqualify", name: "Disqualify" },
  ];

  const relationshipTypeChoices = [
    { id: "Customer", name: "Customer" },
    { id: "Ecosystem", name: "Ecosystem" },
    { id: "Partner", name: "Partner" },
    { id: "Prospect", name: "Prospect" },
    { id: "Referral Source", name: "Referral Source" },
    { id: "Student", name: "Student" },
    { id: "Vendor", name: "Vendor" },
    { id: "Other", name: "Other" },
  ];

  const getChoicesForField = () => {
    switch (selectedField) {
      case "connection_pipeline_stage":
        return (connectionPipelineStages ?? []).map((stage) => ({
          id: stage.value,
          name: stage.label,
        }));
      case "relationship_motion":
        return relationshipTypeChoices;
      case "status":
        return relationshipStrengthChoices;
      case "influence":
        return influenceChoices;
      case "next_relationship_move":
        return nextRelationshipMoveChoices;
      case "company_id":
        return companies.map((c) => ({ id: c.id, name: c.name }));
      default:
        return [];
    }
  };

  const handleApply = async () => {
    if (!selectedField || !selectedValue) return;

    setIsApplying(true);

    try {
      // Map raw value for company or other fields if they need specific casting (e.g. status)
      const fieldName = selectedField;
      const value = selectedValue === "__clear__" ? null : selectedValue;

      // Filter out contacts that already have this value
      const contactsToUpdate = selectedContacts.filter(
        (contact) => (contact as any)[fieldName] !== value,
      );

      await Promise.all(
        contactsToUpdate.map((contact) =>
          update("contacts", {
            id: contact.id,
            data: { [fieldName]: value },
            previousData: contact,
          }),
        ),
      );

      notify(
        contactsToUpdate.length > 0
          ? "Successfully updated %{smart_count} contact(s)"
          : "No contacts needed updating",
        {
          messageArgs: { smart_count: contactsToUpdate.length },
          type: "success",
        },
      );
      closeDialog();
      onUnselectItems();
      refresh();
    } catch (error) {
      notify("Failed to update contacts in bulk", {
        type: "error",
      });
      console.error("Bulk edit failed:", error);
    } finally {
      setIsApplying(false);
    }
  };

  if (!selectedIds.length) {
    return null;
  }

  const isBusy = isApplying || isPendingContacts;
  const valueChoices = getChoicesForField();

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-9"
        onClick={() => setOpen(true)}
      >
        <Edit2 className="size-4 mr-2" />
        Bulk Edit
      </Button>

      <Dialog
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            closeDialog();
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Edit Contacts</DialogTitle>
            <DialogDescription>
              Select a field and a new value to apply to the{" "}
              {selectedIds.length} selected contact(s).
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="field-select">Field</Label>
              <Select
                value={selectedField}
                onValueChange={setSelectedField}
                disabled={isBusy}
              >
                <SelectTrigger id="field-select" className="w-full">
                  <SelectValue placeholder="Select field to update..." />
                </SelectTrigger>
                <SelectContent>
                  {fields.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedField && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="value-select">New Value</Label>
                <Select
                  value={selectedValue}
                  onValueChange={setSelectedValue}
                  disabled={
                    isBusy ||
                    (selectedField === "company_id" && isPendingCompanies)
                  }
                >
                  <SelectTrigger id="value-select" className="w-full">
                    <SelectValue
                      placeholder={
                        selectedField === "company_id" && isPendingCompanies
                          ? "Loading companies..."
                          : "Select new value..."
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__clear__">
                      -- Clear Value / None --
                    </SelectItem>
                    {valueChoices.map((choice) => (
                      <SelectItem key={choice.id} value={String(choice.id)}>
                        {choice.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={closeDialog}
              disabled={isBusy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleApply}
              disabled={isBusy || !selectedField || !selectedValue}
            >
              {isApplying ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Applying...
                </>
              ) : (
                "Apply"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
